import type {
  CashierFiscalProfileBody,
  FiscalAddress,
  LegalEntityBody,
  LocationTaxObjectBody,
  TerminalPrroBody,
} from '@/hooks/use-fiscal-registration';

/**
 * PFC-10 — the registration forms' checks, run before a save so a mistake is
 * shown next to its field. The same rules are enforced by the API
 * (`fiscal-registration.dto.ts`), which stays the authority; these only save
 * a round trip.
 */

export type FieldErrors = Record<string, ValidationMessage>;

/** An i18n key and its baked English. */
export type ValidationMessage = { key: string; en: string };

export const MESSAGE_KEY = {
  required: 'fiscalCabinet.validation.required',
  tinLlc: 'fiscalCabinet.validation.tinLlc',
  tinFop: 'fiscalCabinet.validation.tinFop',
  rnokpp: 'fiscalCabinet.validation.rnokpp',
  signerTin: 'fiscalCabinet.validation.signerTin',
  fopHeadSigner: 'fiscalCabinet.validation.fopHeadSigner',
  taxOffice: 'fiscalCabinet.validation.taxOffice',
  vat: 'fiscalCabinet.validation.vat',
  singleTaxGroup: 'fiscalCabinet.validation.singleTaxGroup',
  postcode: 'fiscalCabinet.validation.postcode',
  email: 'fiscalCabinet.validation.email',
  phone: 'fiscalCabinet.validation.phone',
  katottg: 'fiscalCabinet.validation.katottg',
  objectType: 'fiscalCabinet.validation.objectType',
  objectCode: 'fiscalCabinet.validation.objectCode',
  fiscalNumber: 'fiscalCabinet.validation.fiscalNumber',
  fiscalNumberRequired: 'fiscalCabinet.validation.fiscalNumberRequired',
  digits: 'fiscalCabinet.validation.digits',
  keySerial: 'fiscalCabinet.validation.keySerial',
  keySerialRequired: 'fiscalCabinet.validation.keySerialRequired',
} as const;

type MessageName = keyof typeof MESSAGE_KEY;

const MESSAGE_EN: Record<MessageName, string> = {
  required: 'Required.',
  tinLlc: 'An LLC tax ID is the 8-digit EDRPOU code.',
  tinFop: 'A FOP tax ID is the 10-digit RNOKPP.',
  rnokpp: 'Enter the 10-digit RNOKPP.',
  signerTin: 'Enter an RNOKPP (9 or 10 digits) or a passport number.',
  fopHeadSigner: "A FOP signing as head uses the entrepreneur's own tax ID.",
  taxOffice: 'Enter the four-digit tax office code (0101 to 2899).',
  vat: 'Enter 4 to 12 digits, at least 1000.',
  singleTaxGroup: 'Only with the simplified system: a group from 1 to 4.',
  postcode: 'Enter five digits.',
  email: 'Enter an e-mail address.',
  phone: 'Enter a phone number.',
  katottg: 'Enter UA followed by 17 digits.',
  objectType: 'Enter a code from 1 to 999.',
  objectCode: 'Enter 8 digits: the object type code, then its own number.',
  fiscalNumber: 'Enter the 10-digit fiscal number.',
  fiscalNumberRequired: 'A registered PRRO needs its fiscal number.',
  digits: 'Digits only.',
  keySerial: 'Enter the certificate serial in hexadecimal.',
  keySerialRequired: 'A registered key holder needs the key serial.',
};

const msg = (name: MessageName): ValidationMessage => ({
  key: MESSAGE_KEY[name],
  en: MESSAGE_EN[name],
});

const SIGNER_TIN = /^(\d{10}|\d{9}|[АБВГДЕЄЖЗИІКЛМНОПРСТУФХЦЧШЩЮЯ]{2}\d{6})$/u;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** C_STI: four digits, 0101-2899, district part (last two digits) not 00. */
export function isTaxOfficeCode(code: string): boolean {
  if (!/^\d{4}$/.test(code)) return false;
  const n = Number(code);
  return n >= 101 && n <= 2899 && n % 100 !== 0;
}

const blank = (value: string | null | undefined): boolean =>
  value === null || value === undefined || value.trim() === '';

function checkAddress(
  address: FiscalAddress,
  settlementRequired: boolean,
  errors: FieldErrors,
): void {
  if (!blank(address.postcode) && !/^\d{5}$/.test(address.postcode ?? '')) {
    errors['address.postcode'] = msg('postcode');
  }
  if (settlementRequired && blank(address.settlement)) {
    errors['address.settlement'] = msg('required');
  }
}

export function validateLegalEntity(body: LegalEntityBody): FieldErrors {
  const errors: FieldErrors = {};
  const tinPattern = body.kind === 'llc' ? /^\d{8}$/ : /^\d{10}$/;
  if (!tinPattern.test(body.tin)) errors['tin'] = msg(body.kind === 'llc' ? 'tinLlc' : 'tinFop');
  if (blank(body.name)) errors['name'] = msg('required');
  if (
    body.singleTaxGroup !== null &&
    (body.taxationSystem !== 'simplified' || body.singleTaxGroup < 1 || body.singleTaxGroup > 4)
  ) {
    errors['singleTaxGroup'] = msg('singleTaxGroup');
  }
  if (!blank(body.vatNumber) && !/^[1-9]\d{3,11}$/.test(body.vatNumber ?? '')) {
    errors['vatNumber'] = msg('vat');
  }
  if (!isTaxOfficeCode(body.taxOfficeCode)) errors['taxOfficeCode'] = msg('taxOffice');
  if (blank(body.taxOfficeName)) errors['taxOfficeName'] = msg('required');
  if (blank(body.signerName)) errors['signerName'] = msg('required');
  if (!SIGNER_TIN.test(body.signerTin)) {
    errors['signerTin'] = msg('signerTin');
  } else if (body.kind === 'fop' && body.signerRole === 'head' && body.signerTin !== body.tin) {
    errors['signerTin'] = msg('fopHeadSigner');
  }
  checkAddress(body.address, false, errors);
  if (!blank(body.phone) && !/^[0-9 ()\-+.,;]{4,32}$/.test(body.phone ?? '')) {
    errors['phone'] = msg('phone');
  }
  if (!blank(body.email) && !EMAIL.test(body.email ?? '')) errors['email'] = msg('email');
  return errors;
}

export function validateTaxObject(body: LocationTaxObjectBody): FieldErrors {
  const errors: FieldErrors = {};
  if (blank(body.legalEntityId)) errors['legalEntityId'] = msg('required');
  if (!/^UA\d{17}$/.test(body.katottg)) errors['katottg'] = msg('katottg');
  checkAddress(body.address, true, errors);
  if (
    !Number.isInteger(body.objectTypeCode) ||
    body.objectTypeCode < 1 ||
    body.objectTypeCode > 999
  ) {
    errors['objectTypeCode'] = msg('objectType');
  }
  if (body.dpsObjectCode !== null) {
    const typePart = String(body.objectTypeCode).padStart(3, '0');
    if (
      !/^\d{8}$/.test(body.dpsObjectCode) ||
      !body.dpsObjectCode.startsWith(typePart) ||
      body.dpsObjectCode.endsWith('00000')
    ) {
      errors['dpsObjectCode'] = msg('objectCode');
    }
  }
  return errors;
}

export function validatePrro(body: TerminalPrroBody): FieldErrors {
  const errors: FieldErrors = {};
  if (body.fiscalNumber !== null) {
    if (!/^\d{10}$/.test(body.fiscalNumber)) errors['fiscalNumber'] = msg('fiscalNumber');
  } else if (body.registrationStatus === 'registered') {
    errors['fiscalNumber'] = msg('fiscalNumberRequired');
  }
  if (!/^\d{1,20}$/.test(body.localNumber)) errors['localNumber'] = msg('digits');
  if (blank(body.prroName)) errors['prroName'] = msg('required');
  if (blank(body.prroTypeCode)) errors['prroTypeCode'] = msg('required');
  if (blank(body.offlineModeCode)) errors['offlineModeCode'] = msg('required');
  return errors;
}

export function validateCashierProfile(body: CashierFiscalProfileBody): FieldErrors {
  const errors: FieldErrors = {};
  if (blank(body.legalEntityId)) errors['legalEntityId'] = msg('required');
  if (blank(body.fullName)) errors['fullName'] = msg('required');
  if (!/^\d{10}$/.test(body.tin)) errors['tin'] = msg('rnokpp');
  if (body.keySerial !== null) {
    if (!/^[0-9A-Fa-f]{2,64}$/.test(body.keySerial)) errors['keySerial'] = msg('keySerial');
  } else if (body.registrationStatus === 'registered') {
    errors['keySerial'] = msg('keySerialRequired');
  }
  return errors;
}

/** Trimmed text, with blank as null — the shape the API stores. */
export function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
